// SPDX-License-Identifier: GPL-3.0-only
// Copyright (C) 2026 Kyle Johnson
// This file is part of Hushold.
//
// Hushold is free software: you can redistribute it and/or modify
// it under the terms of the GNU General Public License as
// published by the Free Software Foundation, version 3.

import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

interface BottomSheetProps {
  open: boolean;
  onClose: () => void;
  title?: string;
  children: React.ReactNode;
  // When true the sheet grows to (almost) full height — used for long searchable
  // lists (account/currency pickers). Otherwise it hugs its content.
  tall?: boolean;
}

// DECISION: A single slide-up sheet primitive backs every "native pick / filter /
// overflow" surface on mobile (Phase 6 filters, Phase 7 pickers, the More menu).
// It's the platform-standard modal affordance on both iOS and Android and reads
// far better on a phone than the desktop's centered dialog. Keeps Precision Dark:
// square corners, hairline top border, #1A1D27 surface. Backdrop tap and the grab
// handle both dismiss.
export function BottomSheet({ open, onClose, title, children, tall = false }: BottomSheetProps) {
  const { t } = useTranslation(['common', 'mobile', 'errors']);
  useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex flex-col justify-end">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/60"
        onClick={onClose}
        aria-hidden
      />
      {/* Sheet */}
      <div
        className={`relative bg-bg-surface border-t border-border-strong flex flex-col ${
          tall ? 'h-[85vh]' : 'max-h-[85vh]'
        }`}
        role="dialog"
        aria-modal="true"
      >
        {/* Grab handle */}
        <button
          onClick={onClose}
          className="mx-auto mt-2 mb-1 h-1 w-10 bg-border-strong shrink-0"
          aria-label={t('actions.close')}
        />
        {title && (
          <div className="px-4 py-2 border-b border-border shrink-0">
            <h3 className="text-md font-semibold text-text">{title}</h3>
          </div>
        )}
        <div className="overflow-y-auto native-scroll pb-safe">{children}</div>
      </div>
    </div>
  );
}
